import { useState, useEffect } from 'react';
import { 
  Users, 
  Calendar, 
  Eye,
  AlertCircle
} from 'lucide-react';
import {
  AreaChart,
  Area,
  LineChart,
  Line,
  PieChart,
  Pie,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { dashboardApi } from '../services/dashboardApi';
import type { DashboardStats, MonthlyTrend, DailyTrend } from '../services/dashboardApi';

interface StatCardProps {
  title: string;
  value: number;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  format?: 'number' | 'currency' | 'rating';
}

const StatCard = ({ title, value, icon: Icon, format = 'number' }: StatCardProps) => {
  const formatValue = () => {
    if (format === 'currency') return `$${value.toLocaleString()}`;
    if (format === 'rating') return value.toFixed(1);
    return value.toLocaleString();
  };

  // Color schemes for different stat types
  const getCardStyle = (index: number) => {
    const styles = [
      { bg: 'bg-gradient-to-br from-blue-50 to-blue-100', border: 'border-blue-200', icon: 'text-blue-600', text: 'text-blue-900', shadow: 'shadow-lg hover:shadow-xl' },
      { bg: 'bg-gradient-to-br from-purple-50 to-purple-100', border: 'border-purple-200', icon: 'text-purple-600', text: 'text-purple-900', shadow: 'shadow-lg hover:shadow-xl' },
      { bg: 'bg-gradient-to-br from-green-50 to-green-100', border: 'border-green-200', icon: 'text-green-600', text: 'text-green-900', shadow: 'shadow-lg hover:shadow-xl' },
      { bg: 'bg-gradient-to-br from-orange-50 to-orange-100', border: 'border-orange-200', icon: 'text-orange-600', text: 'text-orange-900', shadow: 'shadow-lg hover:shadow-xl' },
      { bg: 'bg-gradient-to-br from-red-50 to-red-100', border: 'border-red-200', icon: 'text-red-600', text: 'text-red-900', shadow: 'shadow-lg hover:shadow-xl' },
      { bg: 'bg-gradient-to-br from-indigo-50 to-indigo-100', border: 'border-indigo-200', icon: 'text-indigo-600', text: 'text-indigo-900', shadow: 'shadow-lg hover:shadow-xl' },
    ];
    return styles[index % styles.length];
  };

  const style = getCardStyle(Math.floor(Math.random() * 6));

  return (
    <div className={`${style.bg} ${style.border} ${style.shadow} rounded-xl p-6 border-2 transition-all duration-300 hover:scale-105 cursor-default`}>
      <div className="flex flex-row items-center justify-between space-y-0">
        <div className="flex-1">
          <p className={`${style.text} text-sm font-semibold opacity-80 mb-2`}>
            {title}
          </p>
          <p className={`${style.text} text-3xl font-bold tracking-tight`}>
            {formatValue()}
          </p>
        </div>
        <div className={`${style.icon} p-3 rounded-lg bg-white shadow-md`}>
          <Icon className="h-6 w-6" />
        </div>
      </div>
    </div>
  );
};

// Custom Tooltip for Monthly Chart
interface TooltipProps {
  active?: boolean;
  payload?: Array<{ payload: MonthlyTrend & { month: string } }>;
}

const CustomMonthlyTooltip = ({ active, payload }: TooltipProps) => {
  if (active && payload?.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-white p-4 border-2 border-blue-500 rounded-lg shadow-2xl">
        <p className="font-bold text-gray-900 text-sm mb-2">{data.month} 2025</p>
        <p className="text-sm font-semibold text-gray-700 mb-3">Total Bookings: <span className="text-lg text-blue-600">{data.bookings}</span></p>
        <div className="border-t border-gray-300 pt-2 space-y-1 text-xs">
          <div className="flex justify-between">
            <span className="text-green-600 font-semibold">✓ Accepted:</span>
            <span className="font-bold">{data.breakdown.accepted} ({data.metrics.acceptanceRate.toFixed(2)}%)</span>
          </div>
          <div className="flex justify-between">
            <span className="text-blue-600 font-semibold">⊙ Pending:</span>
            <span className="font-bold">{data.breakdown.pending}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-red-600 font-semibold">✕ Cancelled:</span>
            <span className="font-bold">{data.breakdown.cancelled} ({data.metrics.cancellationRate.toFixed(2)}%)</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600 font-semibold">⊘ Rejected:</span>
            <span className="font-bold">{data.breakdown.rejected} ({data.metrics.rejectionRate.toFixed(2)}%)</span>
          </div>
        </div>
      </div>
    );
  }
  return null;
};

// Custom Tooltip for Daily Chart
interface DailyTooltipProps {
  active?: boolean;
  payload?: Array<{ payload: DailyTrend & { day: string | number } }>;
}

const CustomDailyTooltip = ({ active, payload }: DailyTooltipProps) => {
  if (active && payload?.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-white p-4 border-2 border-purple-500 rounded-lg shadow-2xl">
        <p className="font-bold text-gray-900 text-sm mb-2">Day {data.day}</p>
        <p className="text-sm font-semibold text-gray-700 mb-3">Total Bookings: <span className="text-lg text-purple-600">{data.bookings}</span></p>
        <div className="border-t border-gray-300 pt-2 space-y-1 text-xs">
          <div className="flex justify-between">
            <span className="text-green-600 font-semibold">✓ Accepted:</span>
            <span className="font-bold">{data.breakdown.accepted} ({data.metrics.acceptanceRate.toFixed(2)}%)</span>
          </div>
          <div className="flex justify-between">
            <span className="text-blue-600 font-semibold">⊙ Pending:</span>
            <span className="font-bold">{data.breakdown.pending}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-red-600 font-semibold">✕ Cancelled:</span>
            <span className="font-bold">{data.breakdown.cancelled} ({data.metrics.cancellationRate.toFixed(2)}%)</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600 font-semibold">⊘ Rejected:</span>
            <span className="font-bold">{data.breakdown.rejected} ({data.metrics.rejectionRate.toFixed(2)}%)</span>
          </div>
        </div>
      </div>
    );
  }
  return null;
};

export default function Dashboard() {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Stats state
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [monthlyData, setMonthlyData] = useState<(MonthlyTrend & { month: string })[]>([]);
  const [dailyData, setDailyData] = useState<(DailyTrend & { day: string | number })[]>([]);
  const [statusDistribution, setStatusDistribution] = useState<Array<{
    name: string;
    value: number;
    color: string;
  }>>([]);

  useEffect(() => {
    const statusColors: Record<string, string> = {
      'Accepted': '#10b981',
      'Pending': '#f59e0b',
      'Cancelled': '#ef4444',
      'Rejected': '#6b7280',
    };

    const fetchDashboardData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Fetch all data in parallel
        const [statsData, monthlyTrendsData, dailyTrendsData] = await Promise.all([
          dashboardApi.getCurrentMonthStats(),
          dashboardApi.getMonthlyTrends(),
          dashboardApi.getDailyTrends(),
        ]);

        // Set stats
        setStats(statsData);

        // Process monthly trends
        if (monthlyTrendsData.data?.trends) {
          setMonthlyData(monthlyTrendsData.data.trends);
        }

        // Process daily trends
        if (dailyTrendsData.data?.trends) {
          const processedDaily = dailyTrendsData.data.trends.map((trend) => ({
            ...trend,
            day: trend.day || trend.date.split('-')[2],
          }));
          setDailyData(processedDaily);
        }

        // Calculate status distribution
        if (statsData) {
          const distribution = [
            { name: 'Accepted', value: statsData.totalAccepted, color: statusColors['Accepted'] },
            { name: 'Pending', value: statsData.totalPending, color: statusColors['Pending'] },
            { name: 'Cancelled', value: statsData.totalCancelled, color: statusColors['Cancelled'] },
            { name: 'Rejected', value: statsData.totalRejected, color: statusColors['Rejected'] },
          ];
          setStatusDistribution(distribution);
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load dashboard data';
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
            <p className="text-muted-foreground">
              Welcome back, {user?.name}! Here's what's happening at grandPittu today.
            </p>
          </div>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <div>
            <h3 className="font-semibold text-red-900">Error Loading Dashboard</h3>
            <p className="text-sm text-red-800">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground">
            Welcome back, {user?.name}! Here's what's happening at grandPittu today.
          </p>
        </div>
        <Button>
          <Eye className="h-4 w-4 mr-2" />
          View Reports
        </Button>
      </div>

      {/* Summary Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <StatCard
            title="Total Bookings (Year)"
            value={stats.totalBookingsYear}
            icon={Calendar}
          />
          <StatCard
            title="Total Bookings (Month)"
            value={stats.totalBookingsMonth}
            icon={Calendar}
          />
          <StatCard
            title="Total Accepted"
            value={stats.totalAccepted}
            icon={Users}
          />
          <StatCard
            title="Total Pending"
            value={stats.totalPending}
            icon={Calendar}
          />
          <StatCard
            title="Total Cancelled"
            value={stats.totalCancelled}
            icon={Users}
          />
          <StatCard
            title="Total Rejected"
            value={stats.totalRejected}
            icon={Users}
          />
        </div>
      )}

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Booking Bar Chart */}
        <div className="bg-gradient-to-br from-white to-blue-50 rounded-2xl shadow-lg border border-blue-100 hover:shadow-2xl transition-all duration-300 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Monthly Bookings</h2>
              <p className="text-sm text-gray-500 mt-1">Year 2025 Overview</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <span className="text-2xl">📊</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={monthlyData}>
              <defs>
                <linearGradient id="colorBookings" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="month" stroke="#6b7280" />
              <YAxis stroke="#6b7280" />
              <Tooltip content={<CustomMonthlyTooltip />} />
              <Area 
                type="monotone" 
                dataKey="bookings" 
                stroke="#3b82f6" 
                fillOpacity={1} 
                fill="url(#colorBookings)" 
                name="Total Bookings"
              />
            </AreaChart>
          </ResponsiveContainer>
          <p className="text-xs text-gray-500 mt-4 flex items-center gap-2">
            <span>💡</span>
            {' '}
            Hover over the area to see detailed breakdown
          </p>
        </div>

        {/* Daily Booking Line Chart */}
        <div className="bg-gradient-to-br from-white to-purple-50 rounded-2xl shadow-lg border border-purple-100 hover:shadow-2xl transition-all duration-300 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Daily Bookings Trend</h2>
              <p className="text-sm text-gray-500 mt-1">Current Month Activity</p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <span className="text-2xl">📈</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={dailyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="day" stroke="#6b7280" />
              <YAxis stroke="#6b7280" />
              <Tooltip content={<CustomDailyTooltip />} />
              <Line 
                type="monotone" 
                dataKey="bookings" 
                stroke="#a855f7" 
                strokeWidth={2}
                dot={{ fill: '#a855f7', r: 4 }}
                activeDot={{ r: 6 }}
                name="Daily Bookings"
              />
            </LineChart>
          </ResponsiveContainer>
          <p className="text-xs text-gray-500 mt-4 flex items-center gap-2">
            <span>💡</span>
            {' '}
            Hover over points to see detailed breakdown
          </p>
        </div>
      </div>

      {/* Status Distribution and Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Pie Chart */}
        <div className="bg-gradient-to-br from-white to-indigo-50 rounded-2xl shadow-lg border border-indigo-100 hover:shadow-2xl transition-all duration-300 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Status Distribution</h2>
              <p className="text-sm text-gray-500 mt-1">Booking breakdown</p>
            </div>
            <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
              <span className="text-2xl">🥧</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={statusDistribution}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {statusDistribution.map((entry) => (
                  <Cell key={entry.name} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => `${value} bookings`} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Status Summary */}
        <div className="lg:col-span-2">
          <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-lg border border-gray-100 hover:shadow-2xl transition-all duration-300 p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Status Summary</h2>
                <p className="text-sm text-gray-500 mt-1">Real-time booking status</p>
              </div>
              <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                <span className="text-2xl">📋</span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {statusDistribution.map((status) => (
                <div 
                  key={status.name} 
                  className="group relative p-4 rounded-xl border-2 transition-all duration-300 hover:scale-105 cursor-pointer"
                  style={{
                    backgroundColor: `${status.color}15`,
                    borderColor: status.color,
                  }}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div
                      className="w-4 h-4 rounded-full shadow-md"
                      style={{ backgroundColor: status.color }}
                    ></div>
                    <span className="font-bold text-gray-700 text-sm">{status.name}</span>
                  </div>
                  <div className="flex items-end justify-between">
                    <p className="text-3xl font-bold" style={{ color: status.color }}>
                      {status.value}
                    </p>
                    <span className="text-xs font-semibold text-gray-500 opacity-70">
                      {statusDistribution.length > 0 
                        ? `${((status.value / statusDistribution.reduce((sum, s) => sum + s.value, 0)) * 100).toFixed(0)}%`
                        : '0%'
                      }
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Additional Info */}
      {stats && (
        <div className="bg-gradient-to-br from-white to-green-50 rounded-2xl shadow-lg border border-green-100 hover:shadow-2xl transition-all duration-300 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Booking Metrics</h2>
              <p className="text-sm text-gray-500 mt-1">Key performance indicators</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <span className="text-2xl">📊</span>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="group p-5 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl border-2 border-blue-200 hover:shadow-lg transition-all duration-300 hover:scale-105">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold text-blue-700">Avg Guests</p>
                <span className="text-2xl">👥</span>
              </div>
              <p className="text-3xl font-bold text-blue-900">
                {stats.averageGuestPerReservation.toFixed(1)}
              </p>
              <p className="text-xs text-blue-600 mt-2">guests per reservation</p>
            </div>
            <div className="group p-5 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl border-2 border-purple-200 hover:shadow-lg transition-all duration-300 hover:scale-105">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold text-purple-700">Occupancy Rate</p>
                <span className="text-2xl">🎯</span>
              </div>
              <p className="text-3xl font-bold text-purple-900">
                {(stats.occupancyRate * 100).toFixed(1)}%
              </p>
              <p className="text-xs text-purple-600 mt-2">restaurant utilization</p>
            </div>
            <div className="group p-5 bg-gradient-to-br from-green-50 to-green-100 rounded-xl border-2 border-green-200 hover:shadow-lg transition-all duration-300 hover:scale-105">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold text-green-700">Completed</p>
                <span className="text-2xl">✅</span>
              </div>
              <p className="text-3xl font-bold text-green-900">
                {stats.totalCompleted}
              </p>
              <p className="text-xs text-green-600 mt-2">completed bookings</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
